// src/pages/Admin/Views/SEO/StaticTasksManager.jsx
import React, { useEffect, useState } from 'react';
import api from '../../../../services/apiServices';
import { useToast } from '../../../../hooks/use-toast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../components/ui/accordion"; // Assuming path
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Play, CheckCircle2, AlertCircle, Clock } from 'lucide-react';

const StaticTasksManager = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningTaskId, setRunningTaskId] = useState(null);
  const { toast } = useToast();

  const fetchTasks = async () => {
    try {
      const { data } = await api.get('/admin/seo/static-tasks');
      setTasks(data);
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to load tasks." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleRunTask = async (taskId) => {
    setRunningTaskId(taskId);
    try {
      await api.post(`/admin/seo/static-tasks/${taskId}/run`);
      toast({ title: "Success", description: "JSON Generation completed.", variant: "success" });
      fetchTasks(); // Refresh list to get updated status and timestamp
    } catch (error) {
      toast({ 
        variant: "destructive", 
        title: "Generation Failed", 
        description: error.response?.data?.detail || "Check server logs." 
      });
    } finally {
      setRunningTaskId(null);
    }
  };

  // Group tasks by Job Type for the Accordion
  const groupedTasks = tasks.reduce((acc, task) => {
    if (!acc[task.job_type]) acc[task.job_type] = [];
    acc[task.job_type].push(task);
    return acc;
  }, {});

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING': return <Badge variant="secondary" className="bg-amber-100 text-amber-800"><Clock size={14} className="mr-1"/> Pending</Badge>;
      case 'COMPLETED': return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle2 size={14} className="mr-1"/> Up to date</Badge>;
      case 'FAILED': return <Badge variant="destructive"><AlertCircle size={14} className="mr-1"/> Failed</Badge>;
      case 'IN_PROGRESS': return <Badge variant="outline" className="border-blue-500 text-blue-500 animate-pulse">Running...</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  if (loading) return <div className="p-6">Loading Tasks...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Static Content Generators</h1>
        <p className="text-muted-foreground mt-1">
          Manage and trigger pre-rendering of heavy SEO JSON payloads (S3 cache) to reduce database load.
        </p>
      </div>

      <Card>
        <CardHeader className="bg-gray-50/50 border-b pb-4">
          <CardTitle className="text-lg">Generation Targets</CardTitle>
          <CardDescription>Tasks grouped by function. "Pending" means data has changed and a rebuild is recommended.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Accordion type="multiple" defaultValue={["NAV_MENU"]} className="w-full">
            
            {Object.keys(groupedTasks).map((jobType) => (
              <AccordionItem value={jobType} key={jobType} className="border-b-0 px-6">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center justify-between w-full pr-4">
                    <span className="font-semibold text-gray-800">
                      {jobType.replace('_', ' ')}
                    </span>
                    <Badge variant="outline" className="ml-2">
                      {groupedTasks[jobType].filter(t => t.status === 'PENDING').length} Pending
                    </Badge>
                  </div>
                </AccordionTrigger>
                
                <AccordionContent className="pb-6">
                  <div className="rounded-md border overflow-hidden">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-600 border-b">
                        <tr>
                          <th className="px-4 py-3 font-medium">Locale</th>
                          <th className="px-4 py-3 font-medium">Status</th>
                          <th className="px-4 py-3 font-medium">Last Run</th>
                          <th className="px-4 py-3 font-medium text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {groupedTasks[jobType].map((task) => (
                          <tr key={task.id} className="bg-white hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {task.country_code} ({task.language_code.toUpperCase()})
                            </td>
                            <td className="px-4 py-3">
                              {getStatusBadge(task.status)}
                            </td>
                            <td className="px-4 py-3 text-gray-500">
                              {task.last_run_at ? new Date(task.last_run_at).toLocaleString() : 'Never'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button 
                                size="sm" 
                                variant={task.status === 'PENDING' ? 'default' : 'outline'}
                                disabled={runningTaskId === task.id || task.status === 'IN_PROGRESS'}
                                onClick={() => handleRunTask(task.id)}
                              >
                                {runningTaskId === task.id ? 'Building...' : <><Play size={14} className="mr-1.5" /> Run Now</>}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}

            {Object.keys(groupedTasks).length === 0 && (
              <div className="p-8 text-center text-gray-500">No static tasks generated yet.</div>
            )}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaticTasksManager;